import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BellRinging, PaperPlaneTilt, Robot, DeviceMobile, Broadcast } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

const TIERS = ["bronze", "silver", "gold", "king"];
const STATUSES = [
  { id: "em_dia", label: "Em dia" },
  { id: "proximo", label: "Próximo do vencimento" },
  { id: "vencido", label: "Vencido" },
];

const Notifications = () => {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [pushCfg, setPushCfg] = useState({ enabled: false });
  const [open, setOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState({});
  const [sendAsPush, setSendAsPush] = useState(true);
  // Segmented push state
  const [segTitle, setSegTitle] = useState("");
  const [segMessage, setSegMessage] = useState("");
  const [segTiers, setSegTiers] = useState({});
  const [segStatuses, setSegStatuses] = useState({});
  const [segAll, setSegAll] = useState(false);
  const [segBusy, setSegBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: list }, { data: cls }, { data: cfg }] = await Promise.all([
        api.get("/admin/notifications"),
        api.get("/admin/clients"),
        api.get("/push/config").catch(() => ({ data: { enabled: false } })),
      ]);
      setItems(list); setClients(cls); setPushCfg(cfg || { enabled: false });
    } catch { toast.error("Erro ao carregar"); }
  };
  useEffect(() => { load(); }, []);

  const runCron = async () => {
    try { const { data } = await api.post("/cron/run"); toast.success(`Geradas: ${data.created}`); load(); }
    catch { toast.error("Erro"); }
  };

  const send = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return toast.error("Selecione ao menos um cliente");
    if (!title || !message) return toast.error("Título e mensagem obrigatórios");
    try {
      const { data } = await api.post("/admin/notifications/send", { user_ids: ids, title, message });
      const pushNote = sendAsPush && data?.push?.ok
        ? ` · Push enviado a ${data.push.recipients}`
        : sendAsPush
          ? " · (nenhum dispositivo registrado)"
          : "";
      toast.success(`Notificação enviada${pushNote}`);
      setOpen(false); setTitle(""); setMessage(""); setSelected({}); load();
    } catch { toast.error("Erro"); }
  };

  const sendSegmented = async () => {
    if (!segTitle || !segMessage) return toast.error("Título e mensagem obrigatórios");
    const tiers = Object.keys(segTiers).filter((k) => segTiers[k]);
    const statuses = Object.keys(segStatuses).filter((k) => segStatuses[k]);
    if (!segAll && tiers.length === 0 && statuses.length === 0) {
      return toast.error("Selecione um segmento ou marque 'Todos os clientes'");
    }
    setSegBusy(true);
    try {
      const { data } = await api.post("/admin/push/send", {
        title: segTitle,
        message: segMessage,
        tiers: tiers.length ? tiers : null,
        statuses: statuses.length ? statuses : null,
        all_clients: segAll,
      });
      if (data?.push?.ok) {
        toast.success(`Push enviado: ${data.push.recipients}/${data.targets} dispositivos`);
      } else {
        toast.warning(`${data.targets} alvos · ${data.players_reached} com dispositivo · ${data.push?.error || "OK"}`);
      }
      setPushOpen(false); setSegTitle(""); setSegMessage(""); setSegTiers({}); setSegStatuses({}); setSegAll(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar push");
    } finally { setSegBusy(false); }
  };

  const toggleAll = (checked) => {
    const all = {};
    if (checked) clients.forEach((c) => { all[c.id] = true; });
    setSelected(all);
  };

  const userMap = Object.fromEntries(clients.map((c) => [c.id, c.name]));
  const registeredDevices = clients.filter((c) => c.onesignal_player_id).length;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Comunicação</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Notificações</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={runCron} variant="outline" data-testid="notif-run-cron"><Robot className="mr-2" size={16}/>Lembretes automáticos</Button>
          <Button
            onClick={() => setPushOpen(true)}
            disabled={!pushCfg.enabled}
            variant="outline"
            data-testid="notif-push-broadcast-btn"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Broadcast className="mr-2" size={16}/>Push segmentado
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="notif-send-btn" className="bg-blue-600 hover:bg-blue-700 text-white"><PaperPlaneTilt className="mr-2" size={16}/>Enviar mensagem</Button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 text-xs text-zinc-500" data-testid="notif-push-status">
        <DeviceMobile size={16} weight="duotone" className={pushCfg.enabled ? "text-emerald-600" : "text-zinc-400"}/>
        {pushCfg.enabled
          ? <>Push ativo (OneSignal) · {registeredDevices} dispositivo{registeredDevices === 1 ? "" : "s"} registrado{registeredDevices === 1 ? "" : "s"}</>
          : <>Push desativado — configure ONESIGNAL_APP_ID/REST_API_KEY no servidor</>}
      </div>

      <Card className="border-zinc-200 p-0 overflow-hidden card-soft">
        <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50/60 text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">
          Histórico de envios
        </div>
        {items.length === 0 ? (
          <div className="p-10 text-center text-zinc-500">
            <BellRinging size={28} weight="duotone" className="mx-auto mb-2 text-zinc-400"/>
            Nenhuma notificação enviada.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {items.map((n) => (
              <li key={n.id} className="px-5 py-4 flex items-start gap-4">
                <div className={`w-9 h-9 rounded-lg grid place-items-center ${n.type === "auto" ? "bg-blue-50 text-blue-700" : n.type === "push" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {n.type === "auto" ? <Robot size={16} weight="fill"/> : n.type === "push" ? <Broadcast size={16} weight="fill"/> : <PaperPlaneTilt size={16} weight="fill"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-medium">{n.title}</div>
                    <div className="text-xs text-zinc-500">{formatDate(n.created_at)}</div>
                  </div>
                  <div className="text-sm text-zinc-600 mt-1">{n.message}</div>
                  <div className="text-xs text-zinc-500 mt-1">→ {userMap[n.user_id] || n.user_id}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Enviar notificação manual</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Título</Label>
              <Input className="mt-1.5" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="notif-title-input"/>
            </div>
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Mensagem</Label>
              <Textarea className="mt-1.5" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Olá, {nome}! ..." data-testid="notif-message-input"/>
            </div>
            {pushCfg.enabled && (
              <label className="flex items-center justify-between border border-zinc-200 rounded-lg px-3 py-2.5 bg-zinc-50/60">
                <div className="text-sm">
                  <div className="font-medium flex items-center gap-2"><DeviceMobile size={16} weight="duotone" className="text-emerald-600"/> Enviar como push também</div>
                  <div className="text-xs text-zinc-500">Os clientes com app/PWA registrados receberão imediatamente.</div>
                </div>
                <Switch checked={sendAsPush} onCheckedChange={setSendAsPush} data-testid="notif-send-push-toggle"/>
              </label>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Destinatários</Label>
                <button className="text-xs text-blue-600" onClick={() => toggleAll(true)} data-testid="notif-select-all">Selecionar todos</button>
              </div>
              <div className="border border-zinc-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-zinc-100">
                {clients.map((c) => (
                  <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 cursor-pointer">
                    <Checkbox checked={!!selected[c.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [c.id]: !!v }))}/>
                    <div className="flex-1 text-sm">
                      <div className="font-medium flex items-center gap-2">
                        {c.name}
                        {c.onesignal_player_id && <DeviceMobile size={12} weight="fill" className="text-emerald-600"/>}
                      </div>
                      <div className="text-xs text-zinc-500">{c.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={send} data-testid="notif-send-confirm" className="bg-blue-600 hover:bg-blue-700 text-white">Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Segmented push broadcast dialog */}
      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Broadcast size={20} weight="duotone" className="text-emerald-600"/>
              Push segmentado
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Título</Label>
              <Input className="mt-1.5" value={segTitle} onChange={(e) => setSegTitle(e.target.value)} data-testid="push-seg-title"/>
            </div>
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Mensagem</Label>
              <Textarea className="mt-1.5" rows={3} value={segMessage} onChange={(e) => setSegMessage(e.target.value)} data-testid="push-seg-message"/>
            </div>

            <label className="flex items-center justify-between border border-zinc-200 rounded-lg px-3 py-2.5">
              <div className="text-sm font-medium">Enviar para todos os clientes</div>
              <Switch checked={segAll} onCheckedChange={(v) => { setSegAll(v); if (v) { setSegTiers({}); setSegStatuses({}); } }} data-testid="push-seg-all"/>
            </label>

            <div className={segAll ? "opacity-40 pointer-events-none" : ""}>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Por nível de fidelidade</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {TIERS.map((t) => (
                  <label key={t} className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50">
                    <Checkbox checked={!!segTiers[t]} onCheckedChange={(v) => setSegTiers((s) => ({ ...s, [t]: !!v }))} data-testid={`push-seg-tier-${t}`}/>
                    <span className="text-sm capitalize">{t}</span>
                  </label>
                ))}
              </div>

              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold mt-4 block">Por status de pagamento</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                {STATUSES.map((st) => (
                  <label key={st.id} className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50">
                    <Checkbox checked={!!segStatuses[st.id]} onCheckedChange={(v) => setSegStatuses((s) => ({ ...s, [st.id]: !!v }))} data-testid={`push-seg-status-${st.id}`}/>
                    <span className="text-sm">{st.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushOpen(false)}>Cancelar</Button>
            <Button onClick={sendSegmented} disabled={segBusy} data-testid="push-seg-send" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {segBusy ? "Enviando..." : "Enviar push"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Notifications;
